import { useRef, useState, useCallback } from "react";

interface UseDragScrollReturn {
  scrollContainerRef: React.RefObject<HTMLDivElement>;
  isDragging: boolean;
  handleMouseDown: (e: React.MouseEvent) => void;
  handleMouseMove: (e: React.MouseEvent) => void;
  handleMouseUp: () => void;
  handleMouseLeave: () => void;
}

/**
 * Custom hook for mouse drag scrolling on horizontally scrollable containers.
 * Provides drag-to-scroll functionality with cursor feedback.
 *
 * @param scrollSpeed - Multiplier for scroll speed (default: 1.5)
 * @returns Object containing ref, state, and event handlers
 */
export const useDragScroll = (scrollSpeed: number = 1.5): UseDragScrollReturn => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    const container = scrollContainerRef.current;
    if (!container) return;
    setIsDragging(true);
    setStartX(e.pageX - container.offsetLeft);
    setScrollLeft(container.scrollLeft);
  }, []);

  const handleMouseMove = useCallback(
    (e: React.MouseEvent) => {
      if (!isDragging) return;
      e.preventDefault();
      const container = scrollContainerRef.current;
      if (!container) return;
      const x = e.pageX - container.offsetLeft;
      const walk = (x - startX) * scrollSpeed;
      container.scrollLeft = scrollLeft - walk;
    },
    [isDragging, startX, scrollLeft, scrollSpeed]
  );

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  const handleMouseLeave = useCallback(() => {
    setIsDragging(false);
  }, []);

  return {
    scrollContainerRef,
    isDragging,
    handleMouseDown,
    handleMouseMove,
    handleMouseUp,
    handleMouseLeave,
  };
};
